import { useEffect, useState } from 'react'
import DashboardLayout from './layout'
import { useTranslation } from 'react-i18next'
import {
  fetchAllUserStatistics,
  StatisticsResponse,
} from '../../services/statisticsService'
import AuthService from '../../services/login'
import {
  AlertCircle,
  Eye,
  CalendarDays,
  Building,
  Briefcase,
  Users,
} from 'lucide-react'
import { StatCard } from '../components/StatCard'
import CookieManager from '../lib/CookiesManager'

interface ErrorState {
  hasError: boolean
  message: string
}

const StatisticsPage = () => {
  const [stats, setStats] = useState<StatisticsResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<ErrorState>({
    hasError: false,
    message: '',
  })
  const { t } = useTranslation()

  useEffect(() => {
    const fetchStats = async () => {
      const userId = CookieManager.getUserId()
      const token = AuthService.getToken()

      if (!userId || !token) {
        setError({
          hasError: true,
          message: t('auth-required'),
        })
        setIsLoading(false)
        return
      }

      try {
        const data = await fetchAllUserStatistics(userId, token)
        setStats(data)

        // Vérifier si certaines statistiques sont manquantes
        if (data.items.length === 0) {
          setError({
            hasError: false,
            message: t('no-announces-found'),
          })
        } else if (data.items.length < 4) {
          // 4 est le nombre total possible de types d'annonces
          setError({
            hasError: true,
            message: t('errors.stats.partial'),
          })
        } else {
          setError({ hasError: false, message: '' })
        }
      } catch (error: any) {
        console.error('Statistics error:', error)
        setError({
          hasError: true,
          message: t('errors.stats.complete'),
        })
        // Même en cas d'erreur, on utilise les données partielles si disponibles
        if (stats === null) {
          setStats({
            totalViews: 0,
            items: [],
          })
        }
      } finally {
        setIsLoading(false)
      }
    }

    fetchStats()
  }, [t])

  if (isLoading) {
    return (
      <DashboardLayout title={t('dashboard.statistics')}>
        <div className='flex flex-col items-center justify-center h-full gap-3'>
          <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-redpink'></div>
          <span className='text-anthracite text-sm'>{t('loading')}...</span>
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout title={t('dashboard.statistics')}>
      <div className='px-0 space-y-5'>
        <div className='bg-anthracite rounded-lg shadow-lg p-5'>
          <div className='flex items-center justify-between'>
            <div>
              <h2 className='text-white text-sm font-medium mb-2'>
                {t('total-views')}
              </h2>
              <p className='text-4xl font-bold text-white'>
                {stats?.totalViews.toLocaleString() || 0}
              </p>
            </div>
            <div className='p-4 bg-white bg-opacity-20 rounded-full'>
              <Eye className='w-8 h-8 text-white' />
            </div>
          </div>
        </div>

        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
          {stats?.items.map(item => (
            <StatCard
              key={`${item.type}-${item.id}`}
              id={item.id}
              title={item.title}
              views={item.views}
              icon={getIcon(item.type)}
              createdAt={item.createdAt}
              type={item.type}
            />
          ))}
        </div>

        {(!stats?.items || stats.items.length === 0) && !error.hasError && (
          <div className='text-center py-12 bg-white rounded-lg shadow'>
            <Eye className='w-12 h-12 text-gray-400 mx-auto mb-4' />
            <p className='text-gray-500'>{t('no-announces-found')}</p>
          </div>
        )}
      </div>
    </DashboardLayout>
  )
}

const getIcon = (type: string) => {
  switch (type) {
    case 'event':
      return <CalendarDays className='w-6 h-6 text-redpink' />
    case 'establishment':
      return <Building className='w-6 h-6 text-redpink' />
    case 'work':
      return <Briefcase className='w-6 h-6 text-redpink' />
    case 'libertin':
      return <Users className='w-6 h-6 text-redpink' />
    default:
      return <Eye className='w-6 h-6 text-redpink' />
  }
}

export default StatisticsPage
